import React, { useEffect, useRef, useState } from 'react';
import './Home.css';
import { useNavigate } from 'react-router-dom';
import { SOLUTIONS, GROUP_META } from '../data/solutions';

const accelerators = SOLUTIONS.filter((s) => s.category === 'accelerator');
const customerSolutions = SOLUTIONS.filter((s) => s.category === 'solution');

export default function Home() {
  const navigate = useNavigate();
  const mainLogoRef = useRef(null);
  const navbarLogoRef = useRef(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    document.title = 'Inalab';
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Testimonial carousel state
  const testimonials = [
    {
      quote: 'As a result of their thought leadership, industry best practices and process-oriented approach, Inalab has become an integral member of our team.',
      name: 'PEO E CO',
      position: '',
      org: '',
    },
    {
      quote: 'The quality of Inalab\'s ongoing and completed deliverables is outstanding. The on-site resources they provide to support their client and deliver all of their products executed their services with due diligence and high quality.',
      name: 'Contracting Officer',
      position: 'PEO EIS Program Analysis and Integration',
      org: '',
    },
    {
      quote: 'Inalab\'s entire team is committed to providing excellent service that exceeds our expectations. Their staff has the technological and programmatic experience needed to successfully integrate new technology and capabilities into modernization efforts.',
      name: 'PEO E CO',
      position: '',
      org: '',
    },
    {
      quote: 'One of Inalab\'s greatest strengths has been their ability to analyze issues facing the agency and provide us with innovative solutions without guidance from us.',
      name: 'AF CIPS CO',
      position: '',
      org: '',
    },
    {
      quote: 'Inalab has truly been a blessing to the Cyberspace Infrastructure Planning System (CIPS) program effort. We had a good contractor prior to Inalab, but Inalab\'s professionalism has catapulted our software effort to a new high.',
      name: 'Elizabeth Ruckwied',
      position: 'CIPS PMO',
      org: '',
    },
    {
      quote: 'Inalab Consulting\'s quality of work is noteworthy, as they have demonstrated a high level of expertise in managing various tasks, resulting in numerous accomplishments.',
      name: 'AF RMS CPAR',
      position: '',
      org: '',
    },
    {
      quote: 'Inalab Consulting, Inc. team members are very knowledgeable, experienced, and professional.  Inalab was able to evaluate and capture knowledgeable team members from previous support contract(s) and has been able to retain these valued team members (100%) without any turn-over (0%).',
      name: 'NWS COR',
      position: '',
      org: '',
    },
  ];
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState('right'); // 'right' or 'left'
  const [autorotate, setAutorotate] = useState(true);
  const [nextIdx, setNextIdx] = useState(null);
  const animTimeout = useRef();

  useEffect(() => {
    if (!autorotate) return;
    const interval = setInterval(() => {
      triggerTestimonialChange('right', true);
    }, 10000);
    return () => clearInterval(interval);
  }, [autorotate, testimonials.length, testimonialIdx]);

  function triggerTestimonialChange(dir, isAuto) {
    if (!isAuto) setAutorotate(false);
    setDirection(dir);
    setNextIdx(dir === 'right'
      ? (testimonialIdx + 1) % testimonials.length
      : (testimonialIdx - 1 + testimonials.length) % testimonials.length);
    setAnimating(true);
    animTimeout.current && clearTimeout(animTimeout.current);
    animTimeout.current = setTimeout(() => {
      setTestimonialIdx(idx => dir === 'right'
        ? (idx + 1) % testimonials.length
        : (idx - 1 + testimonials.length) % testimonials.length);
      setAnimating(false);
      setNextIdx(null);
    }, 350);
  }

  useEffect(() => {
    const handleScroll = () => {
      if (!mainLogoRef.current || !navbarLogoRef.current) return;
      const rect = mainLogoRef.current.getBoundingClientRect();
      if (rect.bottom <= 0) {
        navbarLogoRef.current.classList.add('visible');
      } else {
        navbarLogoRef.current.classList.remove('visible');
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for contacting Inalab! We will get back to you soon.');
  };

  const handleVideoLoad = () => {
    setIsVideoReady(true);
  };

  return (
    <>
      <div className={`parallax-banner video-banner ${isVideoReady ? 'video-banner-visible' : 'video-banner-hidden'}`}>
        <div className="banner-video-overlay" role="presentation"></div>
        <video
          className="banner-video banner-video-full-cover"
          src={process.env.PUBLIC_URL + '/banner_video.mp4'}
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={handleVideoLoad}
          aria-label="Inalab banner background video"
        />
        <div className="banner-text video-banner-text">
          Accelerating Federal Missions with Intelligence, Security, and Innovation
        </div>
      </div>
      <div className="home">
        <section className="approach-section" aria-labelledby="approach-heading">
          <h2 id="approach-heading">Approach</h2>
          <p className="approach-section-paragraph">Inalab accelerates success for U.S. Federal missions by delivering AI-driven solutions that enhance <b>operational readiness</b> and <b>decision superiority</b>. Our iLAB suite provides a critical advantage by supporting Federal mandates to modernize capabilities and ensure <b>mission effectiveness</b>. We deliver secure, innovative tools that provide a measurable impact on the nation's most critical challenges.</p>
        </section>

        <section
          className="home-solutions-section"
          aria-labelledby="home-solutions-heading"
          ref={mainLogoRef}
          onClick={() => navigate('/solutions')}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/solutions'); }}
        >
          <h2 id="home-solutions-heading" className="home-solutions-title">Our Solutions</h2>
          <p className="home-solutions-intro">
            Mission-ready iLAB Accelerators and Customer Solutions that help Federal agencies
            modernize, secure, and act on their data with speed and confidence.
          </p>
          <div className="home-solutions-groups">
            <div className="home-solution-group">
              <div className="home-solution-eyebrow">{GROUP_META.accelerator.eyebrow}</div>
              <h3 className="home-solution-group-title">{GROUP_META.accelerator.title}</h3>
              <p className="home-solution-group-sub">{GROUP_META.accelerator.sub}</p>
              <div className="home-solution-pills">
                {accelerators.map((s) => (
                  <span className="home-solution-pill" key={s.id}>
                    {s.prefix ? `${s.prefix} ` : ''}{s.name}
                  </span>
                ))}
              </div>
            </div>
            <div className="home-solution-group">
              <div className="home-solution-eyebrow">{GROUP_META.solution.eyebrow}</div>
              <h3 className="home-solution-group-title">{GROUP_META.solution.title}</h3>
              <p className="home-solution-group-sub">{GROUP_META.solution.sub}</p>
              <div className="home-solution-pills">
                {customerSolutions.map((s) => (
                  <span className="home-solution-pill" key={s.id}>
                    {s.prefix ? `${s.prefix} ` : ''}{s.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <span className="home-solutions-cta">Explore our solutions &rarr;</span>
        </section>
        <h2 className="clients-header">Our Clients</h2>
        <div className="partners-img-wrapper">
          <img src={process.env.PUBLIC_URL + '/client_new.png'} alt="Our Partners" className="partners-img" />
        </div>

        {/* What They're Saying testimonial carousel */}
        <section className="testimonials-section">
          <h2 className="testimonials-header">Testimonials</h2>
          <div className="testimonial-carousel-wrapper">
            <div className="testimonial-carousel">
              <div className="testimonial-slide-outer" aria-live="polite">
                {/* Desktop-only arrows inside the card, left/right */}
                <button className="testimonial-arrow testimonial-arrow-desktop left" aria-label="Previous testimonial" onClick={() => triggerTestimonialChange('left', false)}>&#x2039;</button>
                <div className={`testimonial-slide large testimonial-anim-${animating ? direction : 'none'} testimonial-current`}
                     key={testimonialIdx}
                     >
                  <blockquote>{testimonials[testimonialIdx].quote}</blockquote>
                  <div className="testimonial-person">
                    <span className="testimonial-name">{testimonials[testimonialIdx].name}</span><br />
                    {(testimonials[testimonialIdx].position || testimonials[testimonialIdx].org) && (
                      <span className="testimonial-position">
                        {testimonials[testimonialIdx].position}
                        {testimonials[testimonialIdx].position && testimonials[testimonialIdx].org ? ', ' : ''}
                        {testimonials[testimonialIdx].org}
                      </span>
                    )}
                  </div>
                </div>
                {animating && nextIdx !== null && (
                  <div className={`testimonial-slide large testimonial-anim-incoming-${direction} testimonial-next`}
                       key={nextIdx}
                       >
                    <blockquote>{testimonials[nextIdx].quote}</blockquote>
                    <div className="testimonial-person">
                      <span className="testimonial-name">{testimonials[nextIdx].name}</span><br />
                      {(testimonials[nextIdx].position || testimonials[nextIdx].org) && (
                        <span className="testimonial-position">
                          {testimonials[nextIdx].position}
                          {testimonials[nextIdx].position && testimonials[nextIdx].org ? ', ' : ''}
                          {testimonials[nextIdx].org}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <button className="testimonial-arrow testimonial-arrow-desktop right" aria-label="Next testimonial" onClick={() => triggerTestimonialChange('right', false)}>&#x203A;</button>
              </div>
              {/* Mobile-only arrows now below the card */}
              <div className="testimonial-navigation testimonial-navigation-mobile">
                <button className="testimonial-arrow" aria-label="Previous testimonial" onClick={() => triggerTestimonialChange('left', false)}>&#x2039;</button>
                <button className="testimonial-arrow" aria-label="Next testimonial" onClick={() => triggerTestimonialChange('right', false)}>&#x203A;</button>
              </div>
            </div>
          </div>
        </section>
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '2.5rem' }}>
          <a
            href="/contact"
            className="contact-button"
          >
            Contact Us
          </a>
        </div>
      </div>
    </>
  );
} 
